<!--
  Comments on passages of the description (logic/descNote). Selecting
  words of the description offers "Comment". The comment is held as a
  draft for the review by default (a pull request comment, sent in the
  review's body); Post now sends it to the conversation at once. Either
  way it quotes the passage for GitHub, with a marker that brings a posted
  one back here. With a connected agent it can instead be a question to
  it (`onask`), answered under the conversation's box. Each commented passage is highlighted (CSS custom
  highlights: the rendered description is left as it is); clicking one goes
  to its comment. `reveal(id)` shows a comment's passage.
-->
<script lang="ts">
  import { tick } from 'svelte';
  import type { PrStore } from '../state/pr.svelte';
  import { buildDescNoteBody, findPassage, markerFor, parseDescNote, textIndex, type DescMarker } from '../logic/descNote';
  import { mergeText } from '../state/drafts';

  interface Props {
    pr: PrStore;
    /** The rendered description (its `.comment-body`). */
    root: HTMLElement | null;
    /** The comment a passage was clicked for. */
    onjump: (id: string) => void;
    /** The agents that can be asked about the pull request. */
    agents?: readonly { id: string; name: string }[];
    /** Ask agent `to` about the passage `quote`: whether it was sent. */
    onask?: (to: string, text: string, quote: string) => Promise<boolean>;
  }
  let { pr, root, onjump, agents = [], onask }: Props = $props();

  /** The conversation's comments on the description, oldest first. */
  const notes = $derived(
    pr.timeline.flatMap((it) => {
      if (it.__typename !== 'IssueComment') return [];
      const p = parseDescNote(it.body);
      return p ? [{ id: it.id, marker: p.marker, author: it.author?.login ?? 'ghost' }] : [];
    }),
  );

  /** What the description's text is now: read again whenever it is drawn again. */
  let drawn = $state(0);
  $effect(() => {
    const el = root;
    if (!el) return;
    const mo = new MutationObserver(() => drawn++);
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => mo.disconnect();
  });

  /** Each note's passage as a DOM range (none for one whose words the description no longer has). */
  const placed = $derived.by(() => {
    void drawn;
    if (!root || !notes.length) return [];
    const ix = textIndex(root);
    return notes.flatMap((n) => {
      const at = findPassage(ix.text, n.marker);
      const a = at && ix.point(at.start);
      const b = at && ix.point(at.end - 1);
      if (!a || !b) return [];
      const range = document.createRange();
      range.setStart(a.node, a.offset);
      range.setEnd(b.node, b.offset + 1);
      return [{ ...n, range }];
    });
  });

  const HIGHLIGHT = 'desc-note';
  const FLASH = 'desc-note-flash';
  const highlights = () => (typeof CSS !== 'undefined' && 'highlights' in CSS ? CSS.highlights : null);
  $effect(() => {
    const h = highlights();
    if (!h) return;
    h.set(HIGHLIGHT, new Highlight(...placed.map((p) => p.range)));
    return () => h.delete(HIGHLIGHT);
  });

  /** The note whose passage is under viewport point (x, y). */
  function noteAt(x: number, y: number) {
    const pos = document.caretPositionFromPoint?.(x, y);
    const node = pos?.offsetNode ?? document.caretRangeFromPoint?.(x, y)?.startContainer;
    const offset = pos?.offset ?? document.caretRangeFromPoint?.(x, y)?.startOffset ?? 0;
    if (!node) return null;
    // The last one written: the one drawn on top.
    return [...placed].reverse().find((p) => p.range.isPointInRange(node, offset)) ?? null;
  }

  $effect(() => {
    const el = root;
    if (!el) return;
    const click = (e: MouseEvent) => {
      if (e.button !== 0 || !document.getSelection()?.isCollapsed || (e.target as Element).closest('a, button')) return;
      const n = noteAt(e.clientX, e.clientY);
      if (n) onjump(n.id);
    };
    const move = (e: PointerEvent) => {
      const n = noteAt(e.clientX, e.clientY);
      el.style.cursor = n ? 'pointer' : '';
      el.title = n ? `${n.author} commented on this passage: click to see it` : '';
    };
    el.addEventListener('click', click);
    el.addEventListener('pointermove', move);
    return () => {
      el.removeEventListener('click', click);
      el.removeEventListener('pointermove', move);
      el.style.cursor = '';
      el.title = '';
    };
  });

  /** Show comment `id`'s passage: scrolled to the middle and flashed. False when its words are no longer in the description. */
  export async function reveal(id: string): Promise<boolean> {
    await tick();
    const p = placed.find((x) => x.id === id);
    if (!p) return false;
    const r = p.range.getBoundingClientRect();
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollBy({ top: r.top + r.height / 2 - innerHeight / 2, behavior: smooth ? 'smooth' : 'auto' });
    const h = highlights();
    if (h) {
      h.set(FLASH, new Highlight(p.range));
      setTimeout(() => h.delete(FLASH), 1800);
    }
    return true;
  }

  // ---------------------------------------------------------------- writing one

  /** The selection's passage, and where to offer "Comment" (by the selection's end). */
  let chosen: { marker: DescMarker; x: number; y: number } | null = $state(null);
  /** The comment being written, for that passage. */
  let writing: { marker: DescMarker; x: number; y: number } | null = $state(null);
  let text = $state('');
  let box: HTMLTextAreaElement | null = $state(null);
  const draftKey = $derived(`description:${pr.owner}/${pr.repo}#${pr.number}`);

  function readSelection() {
    const sel = document.getSelection();
    if (!root || !sel || sel.isCollapsed || !sel.rangeCount || pr.readOnly) return (chosen = null);
    const r = sel.getRangeAt(0);
    if (!root.contains(r.startContainer) || !root.contains(r.endContainer)) return (chosen = null);
    const ix = textIndex(root);
    const start = ix.offset(r.startContainer, r.startOffset);
    const end = ix.offset(r.endContainer, r.endOffset);
    const marker = start !== null && end !== null && end > start ? markerFor(ix.text, start, end) : null;
    if (!marker) return (chosen = null);
    const rects = r.getClientRects();
    const last = rects[rects.length - 1] ?? r.getBoundingClientRect();
    chosen = { marker, x: last.right, y: last.bottom };
  }

  $effect(() => {
    let frame = 0;
    const changed = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(readSelection);
    };
    const scrolled = () => (chosen = null);
    document.addEventListener('selectionchange', changed);
    addEventListener('scroll', scrolled, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('selectionchange', changed);
      removeEventListener('scroll', scrolled, true);
    };
  });

  async function startWriting() {
    if (!chosen) return;
    writing = chosen;
    chosen = null;
    document.getSelection()?.removeAllRanges();
    await tick();
    box?.focus();
  }

  function cancel() {
    writing = null;
    text = '';
  }

  /** The passage of the comment last sent, for a failed one that comes back. */
  let sentFor: { marker: DescMarker; x: number; y: number } | null = null;
  /** Held for the review by default; posted at once with `now`, or where drafts can't be held. */
  function send(now = false) {
    if (!writing || !text.trim() || pr.readOnly) return;
    const body = buildDescNoteBody({ marker: writing.marker, text, prUrl: pr.pr?.url ?? '' });
    sentFor = writing;
    if (now || pr.draftsOff) void pr.addIssueComment(body, { key: draftKey, text });
    else void pr.addPrComment(body, { key: draftKey, text });
    cancel();
  }
  async function ask(to: string) {
    if (!writing || !text.trim() || !onask) return;
    if (await onask(to, text, writing.marker.quote)) cancel();
  }
  // A failed comment given up comes back to its passage, beside what was typed since.
  $effect(() =>
    pr.writes.listen(draftKey, (t) => {
      if (!writing && sentFor) writing = sentFor;
      text = mergeText(text, t);
    }),
  );

  const place = (p: { x: number; y: number }, w: number) => ({ left: Math.max(16, Math.min(p.x - w / 2, innerWidth - w - 16)), top: Math.min(p.y + 8, innerHeight - 40) });
</script>

{#if chosen && !writing}
  {@const at = place(chosen, 110)}
  <button type="button" class="btn small desc-note-offer" style:left="{at.left}px" style:top="{at.top}px" onmousedown={(e) => e.preventDefault()} onclick={startWriting}>
    Comment
  </button>
{/if}

{#if writing}
  {@const at = place(writing, 420)}
  <div class="desc-note-composer" role="dialog" aria-label="Comment on the description" style:left="{at.left}px" style:top="{at.top}px">
    <blockquote>{writing.marker.quote.length > 160 ? writing.marker.quote.slice(0, 160) + '…' : writing.marker.quote}</blockquote>
    <textarea
      bind:this={box}
      bind:value={text}
      rows="4"
      placeholder="Comment on this passage…"
      aria-label="Comment on this passage"
      onkeydown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          cancel();
        } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault();
          send();
        }
      }}
    ></textarea>
    <div class="desc-note-actions">
      <span class="muted small">{pr.draftsOff ? 'Posted to the conversation, quoting the passage.' : 'Quoting the passage; held for your review.'}</span>
      <button type="button" class="btn small" onclick={cancel}>Cancel</button>
      {#each agents as a (a.id)}
        <button type="button" class="btn small" disabled={!text.trim()} onclick={() => ask(a.id)} title="Ask {a.name} about this passage: only you see the answer, under the conversation">Ask {a.name}</button>
      {/each}
      {#if pr.draftsOff}
        <button type="button" class="btn small primary" disabled={!text.trim()} onclick={() => send(true)} title="Comment (Ctrl/⌘+Enter)">Comment</button>
      {:else}
        <button type="button" class="btn small" disabled={!text.trim()} onclick={() => send(true)} title="Post it to the conversation now, outside your review">Post now</button>
        <button type="button" class="btn small primary" disabled={!text.trim()} onclick={() => send()} title="Hold it for your review (Ctrl/⌘+Enter)">Add to review</button>
      {/if}
    </div>
  </div>
{/if}

<style>
  :global(::highlight(desc-note)) {
    background-color: var(--warnbg);
    text-decoration: underline 2px var(--accent);
    text-underline-offset: 3px;
  }
  :global(::highlight(desc-note-flash)) {
    background-color: var(--accent);
    color: var(--bg);
  }
  .desc-note-offer {
    position: fixed;
    z-index: 60;
    box-shadow: 0 6px 18px #0000002e;
  }
  .desc-note-composer {
    position: fixed;
    z-index: 60;
    width: min(420px, calc(100vw - 32px));
    padding: 10px;
    background: var(--surface);
    border: 1px solid var(--accent);
    border-radius: 8px;
    box-shadow: 0 12px 32px #0000002e;
  }
  .desc-note-composer blockquote {
    margin: 0 0 8px;
    padding: 2px 8px;
    border-left: 3px solid var(--line);
    color: var(--muted);
    font-size: 12px;
    max-height: 4.5em;
    overflow: hidden;
  }
  .desc-note-composer textarea {
    width: 100%;
    box-sizing: border-box;
    min-height: 80px;
    font: 13px/1.5 ui-monospace, SFMono-Regular, monospace;
  }
  .desc-note-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
  }
  .desc-note-actions span {
    margin-right: auto;
  }
</style>
